import React from 'react';
import { Page, Document, StyleSheet, Text, View } from '@react-pdf/renderer';
import PdfHeader from './components/reusable/PdfHeader';
import PageNumber from './components/reusable/PageNumber';
import ServiceTechnicianSection from './components/reusable/ServiceTechnicianSection';
import ServicePartsSection from './components/reusable/ServicePartsSection';

const styles = StyleSheet.create({
    body: {
        padding: 15,
        paddingBottom: 25, // For page number
    },
    section: {
        marginTop: 10,
        padding: 8,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#cccccc',
        borderRadius: 5,
        backgroundColor: '#f8f9fa',
    },
    sectionTitle: {
        fontSize: 12,
        fontFamily: 'Helvetica-Bold',
        marginBottom: 6,
        paddingBottom: 3,
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: '#dee2e6',
        color: '#495057',
        textAlign: 'center',
    },
    compactSection: {
        marginTop: 8,
        padding: 6,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#cccccc',
        borderRadius: 3,
        backgroundColor: '#f8f9fa',
    },
    serviceTypeRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    serviceTypeLabel: {
        fontSize: 10,
        fontFamily: 'Helvetica-Bold',
        color: '#495057',
    },
    serviceTypeValue: {
        fontSize: 10,
        fontFamily: 'Helvetica',
        color: '#212529',
        flex: 1,
    },
    textContent: {
        fontSize: 9,
        fontFamily: 'Helvetica',
        color: '#212529',
        lineHeight: 1.3,
        textAlign: 'left',
    },
    largeTextContent: {
        fontSize: 10,
        fontFamily: 'Helvetica',
        color: '#212529',
        lineHeight: 1.4,
        textAlign: 'left',
    },
    workPerformedSection: {
        minHeight: 80,
    },
    serviceRequestedSection: {
        minHeight: 40,
    },
    adminNotesSection: {
        minHeight: 25,
    },
});

interface ServiceSheetPdfProps {
    data: {
        baseSheet: {
            customerName: string;
            locationName: string;
            requestedBy: string;
            dateServiced: string;
            po: string;
            total: number;
            creator: string;
            company: string;
            dept: string;
            dateCreated: string;
        };
        serviceSheet: {
            serviceType: string;
            serviceRequested: string;
            workPerformed: string;
            adminNotes: string;
            stage: string;
        };
        parts: Array<any>;
        techs: Array<any>;
    };
    user: any;
    sheetType?: 'service' | 'customApplication'; // Determines the sheet title and service type options
}

const ServiceSheetPdf: React.FC<ServiceSheetPdfProps> = ({
    data,
    sheetType = 'service'
}) => {
    const { baseSheet, serviceSheet, parts, techs } = data;

    // Format service type display based on sheet type
    const formatServiceType = (type: string) => {
        if (sheetType === 'customApplication') {
            const customTypeMap: { [key: string]: string } = {
                'coldFog': 'Cold Fog',
                'productive': 'Productive',
                'sprayBar': 'Spray Bar',
                'preseason/disinfect': 'Preseason / Disinfect'
            };
            return customTypeMap[type] || type || 'Not Specified';
        } else {
            const typeMap: { [key: string]: string } = {
                'ventilation': 'Ventilation',
                'refrigeration': 'Refrigeration',
                'electrical': 'Electrical',
                'vfdPump': 'VFD / Pump',
                'serviceCheck': 'Service Check',
                'overtimeVent': 'Overtime Ventilation',
                'overtimeRef': 'Overtime Refrigeration',
                'largeJob': 'Large Job',
                'productive': 'Productive',
                'coldFog': 'Cold Fog',
                'sprayBar': 'Spray Bar',
                'preseason/disinfect': 'Preseason / Disinfect'
            };
            return typeMap[type] || type || 'Not Specified';
        }
    };

    // Create sheet object for header with proper title
    const sheetForHeader = {
        ...baseSheet,
        ...serviceSheet,
    };

    return (
        <Document>
            <Page size="A4" style={styles.body}>
                <PdfHeader company={baseSheet.company} sheet={sheetForHeader} />

                {/* Service Type */}
                {serviceSheet.serviceType && (
                    <View style={styles.compactSection}>
                        <View style={styles.serviceTypeRow}>
                            <Text style={styles.serviceTypeLabel}>{sheetType === 'customApplication' ? 'Custom Application Service Type' : 'Service Type'}:</Text>
                            <Text style={styles.serviceTypeValue}>
                                {formatServiceType(serviceSheet.serviceType)}
                            </Text>
                        </View>
                    </View>
                )}

                {/* Service Requested */}
                <View style={[styles.section, styles.serviceRequestedSection]}>
                    <Text style={styles.sectionTitle}>Service Requested</Text>
                    <Text style={styles.largeTextContent}>
                        {serviceSheet.serviceRequested || 'No service request details provided'}
                    </Text>
                </View>

                {/* Work Performed & Comments */}
                <View style={[styles.section, styles.workPerformedSection]}>
                    <Text style={styles.sectionTitle}>Work Performed & Comments</Text>
                    <Text style={styles.largeTextContent}>
                        {serviceSheet.workPerformed || 'No work performed details provided'}
                    </Text>
                </View>

                {/* Technician Section */}
                {techs && techs.length > 0 && (
                    <ServiceTechnicianSection
                        techs={techs}
                    />
                )}

                {/* Parts Section */}
                {parts && parts.length > 0 && (
                    <ServicePartsSection
                        parts={parts}
                    />
                )}
        
                <PageNumber stage={serviceSheet.stage} sheetType="Service" />
            </Page>
        </Document>
    );
};

export default ServiceSheetPdf;
